const nowIso = () => new Date().toISOString();

const safeArray = (value) => (Array.isArray(value) ? value : []);

const flattenIssues = (jurisdictions = {}) =>
  Object.entries(jurisdictions).flatMap(([jurisdiction, entry]) =>
    safeArray(entry?.issues).map((issue) => ({
      jurisdiction,
      code: issue.code,
      severity: issue.severity,
      blocking: Boolean(issue.blocking),
      scope: issue.scope,
      message: issue.message,
      source: issue.source || "",
      sourceUrl: issue.sourceUrl || "",
      sourceQuote: issue.sourceQuote || "",
    }))
  );

const ingredientRisk = (ingredient) => {
  if (!ingredient.validIngredient) return "identity_review";
  if (ingredient.concentration_verified !== true) return "concentration_review";
  if (ingredient.above_threshold) return "ifra_threshold_exceeded";
  return "screened";
};

export const buildFormulaRiskReport = ({
  product = {},
  compliance,
  inputText = "",
  generatedAt = nowIso(),
} = {}) => {
  const activeFormula = compliance?.activeFormula || {};
  const jurisdictions = compliance?.jurisdictions || {};
  const issues = flattenIssues(jurisdictions);
  const ingredients = safeArray(activeFormula.ifraResults).map((ingredient) => ({
    name: ingredient.ingredient_name,
    casNumber: ingredient.cas_number,
    formulaPercentage: ingredient.formula_percentage,
    concentrationUnit: ingredient.concentration_unit || "",
    concentrationVerified: ingredient.concentration_verified === true,
    concentrationIssue: ingredient.concentration_issue || "",
    ifraCategory4Threshold: ingredient.category_4_threshold,
    ifraAboveThreshold: Boolean(ingredient.above_threshold),
    validIngredient: Boolean(ingredient.validIngredient),
    matchType: ingredient.match_type || "",
    risk: ingredientRisk(ingredient),
    justification: ingredient.justification || "",
  }));

  const blockingIssues = issues.filter((issue) => issue.blocking);
  const warningIssues = issues.filter((issue) => !issue.blocking);
  const unknownIngredients = ingredients.filter((ingredient) => !ingredient.validIngredient);
  const unverifiedConcentrations = ingredients.filter(
    (ingredient) => ingredient.concentrationVerified !== true
  );
  const ifraThresholdBreaches = ingredients.filter((ingredient) => ingredient.ifraAboveThreshold);
  const euAnnexIssues = issues.filter((issue) => String(issue.code || "").startsWith("EU_ANNEX"));
  const allergenIssues = issues.filter((issue) => issue.code === "EU_LABEL_ALLERGEN_REVIEW");
  const euRuleSet = jurisdictions.EU?.facts?.ruleSet || {};

  return {
    reportType: "Formula Risk Report",
    generatedAt,
    disclaimer:
      "Screening report only. This is not final legal certification, CPSR approval, PIF approval, CPNP submission, IFRA certificate replacement, or legal advice.",
    product: {
      id: product.id || "",
      name: product.name || activeFormula.name || "Formula preview",
      brand: product.brand || "",
      category: product.category || "",
      function: product.function || "",
      markets: product.markets || [],
    },
    formula: {
      id: activeFormula.id || "",
      name: activeFormula.name || product.name || "Formula preview",
      source: activeFormula.source || "",
      inputText: inputText || activeFormula.inputText || "",
      ingredientCount: ingredients.length,
    },
    summary: {
      overallStatus: compliance?.overallStatus || "Draft",
      totalIssues: issues.length,
      blockingIssues: blockingIssues.length,
      warningIssues: warningIssues.length,
      validIngredients: ingredients.filter((ingredient) => ingredient.validIngredient).length,
      unknownIngredients: unknownIngredients.length,
      unverifiedConcentrations: unverifiedConcentrations.length,
      ifraThresholdBreaches: ifraThresholdBreaches.length,
      euAnnexIssues: euAnnexIssues.length,
      euAllergenLabelReviews: allergenIssues.length,
    },
    jurisdictionStatus: Object.fromEntries(
      Object.entries(jurisdictions).map(([jurisdiction, entry]) => [
        jurisdiction,
        {
          status: entry.status,
          issueCount: safeArray(entry.issues).length,
          blockingIssueCount: safeArray(entry.issues).filter((issue) => issue.blocking).length,
        },
      ])
    ),
    ingredients,
    issues,
    unfolded: activeFormula.unfolded || { constituents: [], aggregates: [] },
    sourceSnapshot: {
      euRuleSet: {
        complete: Boolean(euRuleSet.complete),
        source: euRuleSet.source || "",
        counts: euRuleSet.counts || {},
        metadata: euRuleSet.metadata || {},
      },
    },
    reviewQueues: {
      blockingIssues,
      warningIssues,
      unknownIngredients,
      unverifiedConcentrations,
      ifraThresholdBreaches,
      euAnnexIssues,
      allergenIssues,
    },
  };
};

const tableRow = (values) => `| ${values.map((value) => String(value ?? "").replace(/\n/g, " ")).join(" | ")} |`;

export const serializeFormulaRiskReportMarkdown = (report) => {
  const lines = [
    `# ${report.reportType}`,
    "",
    `Generated: ${report.generatedAt}`,
    "",
    `Product: ${report.product.name}`,
    `Formula: ${report.formula.name}`,
    `Status: ${report.summary.overallStatus}`,
    "",
    "## Summary",
    "",
    tableRow(["Metric", "Value"]),
    tableRow(["---", "---"]),
    tableRow(["Total issues", report.summary.totalIssues]),
    tableRow(["Blocking issues", report.summary.blockingIssues]),
    tableRow(["Warning issues", report.summary.warningIssues]),
    tableRow(["Unknown ingredients", report.summary.unknownIngredients]),
    tableRow(["Unverified concentrations", report.summary.unverifiedConcentrations]),
    tableRow(["IFRA threshold breaches", report.summary.ifraThresholdBreaches]),
    tableRow(["EU Annex issues", report.summary.euAnnexIssues]),
    "",
    "## Ingredients",
    "",
    tableRow(["Ingredient", "CAS", "Percent", "Unit", "Verified", "IFRA Cat 4", "Risk"]),
    tableRow(["---", "---", "---", "---", "---", "---", "---"]),
    ...report.ingredients.map((ingredient) =>
      tableRow([
        ingredient.name,
        ingredient.casNumber,
        ingredient.formulaPercentage,
        ingredient.concentrationUnit,
        ingredient.concentrationVerified ? "yes" : "no",
        ingredient.ifraCategory4Threshold,
        ingredient.risk,
      ])
    ),
    "",
    "## Issues",
    "",
    tableRow(["Jurisdiction", "Code", "Severity", "Blocking", "Message", "Source"]),
    tableRow(["---", "---", "---", "---", "---", "---"]),
    ...report.issues.map((issue) =>
      tableRow([
        issue.jurisdiction,
        issue.code,
        issue.severity,
        issue.blocking ? "yes" : "no",
        issue.message,
        issue.source,
      ])
    ),
    "",
    "## Source Snapshot",
    "",
    `EU rule source: ${report.sourceSnapshot.euRuleSet.source || "n/a"}`,
    `EU rule count: ${report.sourceSnapshot.euRuleSet.counts?.total || 0}`,
    `EU snapshot version: ${report.sourceSnapshot.euRuleSet.metadata?.version || "n/a"}`,
    "",
    "## Disclaimer",
    "",
    report.disclaimer,
    "",
  ];

  return lines.join("\n");
};

export const serializeFormulaRiskReportJson = (report) => JSON.stringify(report, null, 2);

export const downloadTextFile = ({ filename, content, type = "text/plain" }) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};
